import moment from 'moment-timezone';
import { TIME_ZONE } from '@silvergatedelivery/constants';
import JSZip from 'jszip';
import { download } from 'utilities/file';
import { formatAddress } from 'utilities/format';
import { numberToChineseUpper } from '../CountyPrsReport/workbooks/helpers';
import ExcelJS from 'exceljs';
import { toastr } from 'react-redux-toastr';
import { Storage } from 'aws-amplify';
import { downloadImage, loadImage } from 'utilities/image';

const stampSetting = {
  'e281a800-8ae3-11ec-823f-bbd2f0442830': { // 銀色大門-苗栗
    company: {
      file: '/images/receipt/e281a800-8ae3-11ec-823f-bbd2f0442830/company.png',
      width: 150,
      height: 150,
    },
    chairman: {
      file: '/images/receipt/e281a800-8ae3-11ec-823f-bbd2f0442830/chairman.png',
      width: 50,
      height: 50,
    },
    accountant: {
      file: '/images/receipt/e281a800-8ae3-11ec-823f-bbd2f0442830/accountant.png',
      width: 50,
      height: 50,
    },
    handler: {
      file: '/images/receipt/e281a800-8ae3-11ec-823f-bbd2f0442830/handler.png',
      width: 50,
      height: 50,
    },
  },
};

const getSanitizedElderName = (name) => {
  const index = name.indexOf('(');
  const result = index !== -1 ? name.substring(0, index).trim() : name;
  return result
    .replace(/\\/g, '')
    .replace(/\//g, '')
    .replace(/\?/g, '')
    .replace(/\*/g, '')
    .replace(/\[/g, '')
    .replace(/\]/g, '');
};

export default async function issueReceipts({
  county: inCounty,
  elders: allElders,
  orders: allOrders,
  selectedMonth = 'YYYY-MM',
  client,
  includeUndeliveredOrders = false,
  excludeNonFoodDeliveryOrders = false,
  selectedPaymentMethod = 'all',
  groupByDistrict = false,
}) {
  const { name, facilitySettings: {
    dba, companyStampS3Keys, chairmanStampS3Keys, accountantStampS3Keys, handlerStampS3Keys, receiptInfo,
  } } = client;
  const {
    receiptType = 'triplicate', oneCopyOneSheet = false, reverseCopyType = false,
    maskIdentificationCardId = false, hideIdentificationCardId = false, hideCaseNumber = false, hideCmsLevel = false, hideAccountant = false,
  } = receiptInfo || {};
  const copyCount = receiptType === 'triplicate' ? 3 : 2;
  let copyType = {
    duplicate: ['存根聯', '個案收執聯'],
    triplicate: ['存根聯', '會計聯', '個案收執聯'],
  };
  if (reverseCopyType) {
    copyType = {
      duplicate: copyType.duplicate.reverse(),
      triplicate: copyType.triplicate.reverse(),
    };
  }
  const clientName = dba || name;
  let elders = allElders.filter(({ address }) => address.county === inCounty);
  if (selectedPaymentMethod !== 'all') {
    if (selectedPaymentMethod === 'none') {
      elders = elders.filter(({ paymentMethod }) => paymentMethod === null);
    } else {
      elders = elders.filter(({ paymentMethod }) => paymentMethod === selectedPaymentMethod);
    }
  }
  if (elders.length == 0) {
    toastr.error('沒有符合設定條件的送餐對象');
  }
  let includedOrderStatus = ['delivered', 'completed'];
  if (includeUndeliveredOrders) {
    includedOrderStatus = includedOrderStatus.concat(['ready', 'readyForPickup', 'delivering']);
  }
  let orders = allOrders
    .filter(({ elderId }) => elders.some(({ id }) => id === elderId))
    .filter(({ status }) => includedOrderStatus.includes(status));
  if (excludeNonFoodDeliveryOrders) {
    orders = orders.filter(({ category }) => category === '送餐' || category === '送餐和物資');
  }
  if (orders.length === 0) {
    toastr.error('沒有符合設定條件的訂單');
  }
  const zip = new JSZip();
  const [year, month, day] = moment().tz(TIME_ZONE).format('YYYY-MM-DD').split('-');
  const yearMonthDayTitle = `中華民國${parseInt(year)-1911}年${month}月${day}日`;

  const stamp = stampSetting[client.id] || {};
  if (stamp) {
    await Promise.all(Object.keys(stamp).map(async (key) => {
      if (stamp[key] && stamp[key].file) {
        stamp[key].imageBuffer = await downloadImage(stamp[key].file);
      }
    }));
  }
  if (companyStampS3Keys) {
    const uri = await Storage.get(companyStampS3Keys[0]);
    const { imageBuffer, width, height } = await loadImage(uri, 150);
    if (imageBuffer) {
      stamp.company = {
        file: uri,
        imageBuffer,
        width,
        height,
      };
    }
  }
  if (chairmanStampS3Keys) {
    const uri = await Storage.get(chairmanStampS3Keys[0]);
    const { imageBuffer, width, height } = await loadImage(uri, 100);
    if (imageBuffer) {
      stamp.chairman = {
        file: uri,
        imageBuffer,
        width,
        height,
      };
    }
  }
  if (accountantStampS3Keys) {
    const uri = await Storage.get(accountantStampS3Keys[0]);
    const { imageBuffer, width, height } = await loadImage(uri, 100);
    if (imageBuffer) {
      stamp.accountant = {
        file: uri,
        imageBuffer,
        width,
        height,
      };
    }
  }
  if (handlerStampS3Keys) {
    const uri = await Storage.get(handlerStampS3Keys[0]);
    const { imageBuffer, width, height } = await loadImage(uri, 100);
    if (imageBuffer) {
      stamp.handler = {
        file: uri,
        imageBuffer,
        width,
        height,
      };
    }
  }
  let eldersGroup = elders.reduce((acc, elder) => {
    const key = elder.address.district;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(elder);
    return acc;
  }, {});
  if (!groupByDistrict) {
    eldersGroup = { '': elders };
  }
  const bufferData = [];
  let receiptNo = 0;
  await Object.keys(eldersGroup).sort().reduce(async (chain, district) => {
    await chain;

    const elders = eldersGroup[district];
    const workbooks = [];
    const stampImages = [];
    Array(copyCount).fill().forEach(() => {
      workbooks.push(new ExcelJS.Workbook());
      stampImages.push({});
    });
    const worksheetName = [];

    const getExtensionFromURL = (url) => {
      const match = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
      return match ? match[1] : 'png';
    };

    if (stamp) {
      Object.keys(stamp).forEach((key) => {
        if (stamp[key].imageBuffer) {
          const extension = getExtensionFromURL(stamp[key].file);
          workbooks.forEach((workbook, index) => {
            const imageId = workbook.addImage({
              buffer: stamp[key].imageBuffer,
              extension,
            });
            stampImages[index][key] = {};
            stampImages[index][key].width = stamp[key].width;
            stampImages[index][key].height = stamp[key].height;
            stampImages[index][key].imageId = imageId;
          });
        }
      });
    }
    elders.sort((a, b) => a.name > b.name ? 1 : -1).forEach((elder) => {
      const { id, name } = elder;
      const elderOrders = orders.filter(({ elderId }) => elderId === id);
      if (elderOrders.length === 0) {
        return;
      }
      let sanitizedElderName = getSanitizedElderName(name);
      let duplicatedNameCount = 0;
      if (worksheetName.includes(sanitizedElderName)) {
        duplicatedNameCount += 1;
        sanitizedElderName = sanitizedElderName + `(${duplicatedNameCount})`;
      } else {
        duplicatedNameCount = 0;
      }
      if (!oneCopyOneSheet) {
        const worksheet = workbooks[0].addWorksheet(sanitizedElderName, {
          pageSetup: {
            paperSize: 9,
            fitToPage: true,
            fitToWidth: 1,
            horizontalCentered: true,
            verticalCentered: true,
            orientation: 'portrait',
          },
        });
        const result = getWorksheetElder(
          worksheet, client, elder, elderOrders, yearMonthDayTitle, inCounty, selectedMonth,
          receiptNo, stampImages[0], -1, maskIdentificationCardId,
          hideIdentificationCardId, hideCaseNumber, hideCmsLevel, hideAccountant,
        );
        worksheet.pageSetup.margins = {
          left: 0.236, right: 0.236,
          top: 0.748, bottom: 0.748,
          header: 0.3, footer: 0.3,
        };
        if (!result) {
          workbooks[0].removeWorksheet(worksheet.id);
        } else {
          receiptNo += 1;
          worksheetName.push(sanitizedElderName);
        }
      } else {
        Array(copyCount).fill().forEach((_, copyIndex) => {
          const worksheet = workbooks[copyIndex].addWorksheet(`${sanitizedElderName}`, {
            pageSetup: {
              paperSize: 11,
              fitToPage: true,
              fitToWidth: 1,
              horizontalCentered: true,
              verticalCentered: true,
              orientation: 'landscape',
            },
          });
          const result = getWorksheetElder(
            worksheet, client, elder, elderOrders, yearMonthDayTitle, inCounty, selectedMonth,
            receiptNo, stampImages[copyIndex], copyIndex, maskIdentificationCardId,
            hideIdentificationCardId, hideCaseNumber, hideCmsLevel, hideAccountant,
          );
          worksheet.pageSetup.margins = {
            left: 0.236 / 2, right: 0.236 / 2,
            top: 0.748 / 2, bottom: 0.748 / 2,
            header: 0.3 / 2, footer: 0.3 / 2,
          };
          if (!result) {
            workbooks[copyIndex].removeWorksheet(worksheet.id);
            return;
          } else {
            if (copyIndex === copyCount - 1) {
              receiptNo += 1;
              worksheetName.push(sanitizedElderName);
            }
          }
        });
      }
    });
    if (!oneCopyOneSheet) {
      try {
        if (workbooks[0] && workbooks[0].worksheets && workbooks[0].worksheets.length > 0) {
          const bufferDataByDistrict = await workbooks[0].xlsx.writeBuffer();
          if (process.env.JEST_WORKER_ID) {
            bufferData.push(bufferDataByDistrict);
            return;
          }
          zip.file(`${inCounty}${district}餐飲服務費收據(自費戶)__${selectedMonth}.xlsx`, bufferDataByDistrict, { binary: true });
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      try {
        await Promise.all(Array(copyCount).fill().map(async (_, copyIndex) => {
          if (workbooks[copyIndex] && workbooks[copyIndex].worksheets && workbooks[copyIndex].worksheets.length > 0) {
            const bufferDataByDistrict = await workbooks[copyIndex].xlsx.writeBuffer();
            if (process.env.JEST_WORKER_ID) {
              bufferData.push(bufferDataByDistrict);
              return;
            }
            zip.file(`${inCounty}${district}餐飲服務費收據(自費戶)__${selectedMonth}_${copyType[receiptType][copyIndex]}.xlsx`, bufferDataByDistrict, { binary: true });
          }
        }));
      } catch (e) {
        console.error(e);
      }
    }
  }, Promise.resolve());

  if (process.env.JEST_WORKER_ID) {
    return bufferData;
  }
  const files = Object.keys(zip.files);
  if (files.length !== 0) {
    const zipContent = zip.generate({ type: 'blob' });
    download(`${clientName}__${selectedMonth}餐飲服務費收據(自費戶).zip`, zipContent);
  }
}

function getWorksheetElder(
  worksheet, client, elder, elderOrders = [], yearMonthDayTitle, county, selectedMonth,
  receiptNo, stampImages, copyIndex = -1, maskIdentificationCardId = false,
  hideIdentificationCardId = false, hideCaseNumber = false, hideCmsLevel = false, hideAccountant = false,
) {
  const {
    name, facilitySettings: { dba, receiptInfo }, address: clientAddress, phoneNumber,
  } = client;
  const { receiptType = 'triplicate', taxIdNumber, faxNumber,
    bankName, accountNumber, accountName,
    bankName2, accountNumber2, accountName2,
    bankName3, accountNumber3, accountName3,
    chairmanName='', accountantName='', handlerName='',
    note, managerTitle, reverseCopyType } = receiptInfo || {};
  const clientName = dba || name;
  const elderRows = [];
  const {
    name: elderName,
    identificationCardId,
    cmsLevel,
    caseNumber,
    paymentBankIndex,
  } = elder;
  let displayedIdNumber = identificationCardId;
  if (maskIdentificationCardId) {
    if (identificationCardId && identificationCardId.length >= 4) {
      displayedIdNumber = identificationCardId.slice(0, -4) + '****';
    } else {
      displayedIdNumber = '****';
    }
  }
  let displayedBankName = bankName;
  let displayedAccountNumber = accountNumber;
  let displayedAccountName = accountName;
  if (paymentBankIndex === 2) {
    displayedBankName = bankName2;
    displayedAccountNumber = accountNumber2;
    displayedAccountName = accountName2;
  } else if (paymentBankIndex === 3) {
    displayedBankName = bankName3;
    displayedAccountNumber = accountNumber3;
    displayedAccountName = accountName3;
  }

  let total = 0;
  if (elderOrders.length === 0) {
    return;
  }

  elderOrders.forEach(({ total: orderTotal, mealItems }) => {
    let orderTotalWithoutSurcharge = orderTotal;
    mealItems.forEach(({ surcharges }) => {
      if (surcharges) {
        surcharges.forEach(({ name, price }) => {
          const rowIndex = elderRows.findIndex((elderRow) => elderRow['服務編號'] === name && elderRow['自費單價'] === price);
          if (rowIndex === -1) {
            elderRows.push({
              服務編號: name,
              福利身份別: '全自費',
              自費單價: price,
              自費次數: 1,
              小計: price,
            });
          } else {
            elderRows[rowIndex]['自費次數'] += 1;
            elderRows[rowIndex]['小計'] += price;
          }
          orderTotalWithoutSurcharge -= price;
        });
      }
    });

    const rowIndex = elderRows.findIndex((elderRow) => elderRow['自費單價'] === orderTotalWithoutSurcharge);
    if (rowIndex === -1) {
      elderRows.push({
        服務編號: '餐點費用',
        福利身份別: '全自費',
        自費單價: orderTotalWithoutSurcharge,
        自費次數: 1,
        小計: orderTotalWithoutSurcharge,
      });
    } else {
      elderRows[rowIndex]['自費次數'] += 1;
      elderRows[rowIndex]['小計'] += orderTotalWithoutSurcharge;
    }

    total += orderTotal;
  });

  const serviceDates = elderOrders.map(({ date, mealSlot }) => {
    const formattedDate = moment(date).format('MM/DD');
    return {
      date: formattedDate,
      slot: mealSlot === 'lunch'? '午' : '晚',
    };
  }).sort((a, b) => {
    if (a.date > b.date) return 1;
    if (a.date < b.date) return -1;
    if (a.slot === '午') return -1;
  });
  let serviceDatesString = '';
  let previousDate = '';
  let serviceDays = 0;
  serviceDates.forEach(({ date, slot }, index) => {
    if (previousDate !== date) {
      if (index !== 0) {
        serviceDatesString += '、';
      }
      serviceDatesString += date + ' ' + slot;
      serviceDays += 1;
    } else {
      serviceDatesString += slot;
    }
    previousDate = date;
  });

  const elderRowsCount = elderRows.length;

  if (elderRowsCount === 0) {
    return;
  }

  let copyType = {
    duplicate: [
      '一\n式\n二\n聯\n \n \n第\n一\n聯\n：\n存\n根\n聯',
      '一\n式\n二\n聯\n \n \n第\n二\n聯\n：\n個\n案\n收\n執\n聯',
    ],
    triplicate: [
      '一\n式\n三\n聯\n \n \n第\n一\n聯\n：\n存\n根\n聯',
      '一\n式\n三\n聯\n \n \n第\n二\n聯\n：\n會\n計\n聯',
      '一\n式\n三\n聯\n \n \n第\n三\n聯\n：\n個\n案\n收\n執\n聯',
    ],
  };
  if (reverseCopyType) {
    copyType = {
      duplicate: [
        '一\n式\n二\n聯\n \n \n第\n一\n聯\n：\n個\n案\n收\n執\n聯',
        '一\n式\n二\n聯\n \n \n第\n二\n聯\n：\n存\n根\n聯',
      ],
      triplicate: [
        '一\n式\n三\n聯\n \n \n第\n一\n聯\n：\n個\n案\n收\n執\n聯',
        '一\n式\n三\n聯\n \n \n第\n二\n聯\n：\n會\n計\n聯',
        '一\n式\n三\n聯\n \n \n第\n三\n聯\n：\n存\n根\n聯',
      ],
    };
  }
  if (copyIndex !== -1) {
    copyType[receiptType] = [copyType[receiptType][copyIndex]];
  }
  worksheet.columns = [
    { header: '', key: 'A', width: 6.15 },
    { header: '', key: 'B', width: 5.78 },
    { header: '', key: 'C', width: 4.70 },
    { header: '', key: 'D', width: 6.15 },
    { header: '', key: 'E', width: 6.15 },
    { header: '', key: 'F', width: 9.68 },
    { header: '', key: 'G', width: 6.03 },
    { header: '', key: 'H', width: 6.03 },
    { header: '', key: 'I', width: 6.03 },
    { header: '', key: 'J', width: 6.03 },
    { header: '', key: 'K', width: 6.03 },
    { header: '', key: 'L', width: 6.03 },
    { header: '', key: 'M', width: 6.03 },
    { header: '', key: 'N', width: 6.03 },
    { header: '', key: 'O', width: 6.03 },
    { header: '', key: 'P', width: 6.03 },
    { header: '', key: 'Q', width: 6.03 },
    { header: '', key: 'R', width: 6.03 },
    { header: '', key: 'S', width: 2.75 },
  ];

  let rowIndex = 1;
  let copyCount = 0;
  const defaultRowHeight = 15.75;
  const fontName = 'Microsoft YaHei';
  while (copyCount < copyType[receiptType].length) {
    worksheet.mergeCells(`A${rowIndex}:S${rowIndex}`);
    worksheet.getCell(`A${rowIndex}`).value = clientName;
    worksheet.getCell(`A${rowIndex}`).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    worksheet.getCell(`A${rowIndex}`).font = { bold: true, size: 16, name: fontName };
    worksheet.getRow(rowIndex++).height = 23.25 * Math.ceil(clientName.length / 40);
    worksheet.getRow(rowIndex++).height = 17.25;

    worksheet.mergeCells(`A${rowIndex}:E${rowIndex}`);
    worksheet.getCell(`A${rowIndex}`).value = `流水號編號：S${selectedMonth.replace('-', '')}${receiptNo.toString().padStart(3, '0')}`;
    worksheet.getCell(`A${rowIndex}`).font = { size: 9, name: fontName };
    worksheet.getCell(`A${rowIndex}`).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(`F${rowIndex}:M${rowIndex}`);
    worksheet.getCell(`F${rowIndex}`).value = '收據明細';
    worksheet.getCell(`F${rowIndex}`).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getCell(`F${rowIndex}`).font = { size: 10, name: fontName };
    worksheet.mergeCells(`N${rowIndex}:R${rowIndex}`);
    worksheet.getCell(`N${rowIndex}`).value = `列表日：${yearMonthDayTitle}`;
    worksheet.getCell(`N${rowIndex}`).font = { size: 9, name: fontName };
    worksheet.getCell(`N${rowIndex}`).alignment = { horizontal: 'right', vertical: 'middle' };
    worksheet.getRow(rowIndex++).height = defaultRowHeight;

    worksheet.mergeCells(`A${rowIndex}:E${rowIndex}`);
    if (!hideIdentificationCardId) {
      worksheet.getCell(`A${rowIndex}`).value = `身分證字號：${displayedIdNumber || ''}`;
      worksheet.getCell(`A${rowIndex}`).font = { size: 9, name: fontName };
      worksheet.getCell(`A${rowIndex}`).alignment = { horizontal: 'left', vertical: 'middle' };
    }

    worksheet.mergeCells(`F${rowIndex}:M${rowIndex + 1}`);
    worksheet.getCell(`F${rowIndex}`).value = `${selectedMonth.replace('-', '年')}月 長期照顧營養餐飲服務費`;
    worksheet.getCell(`F${rowIndex}`).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getCell(`F${rowIndex}`).font = { bold: true, size: 11, name: fontName };

    worksheet.mergeCells(`N${rowIndex}:R${rowIndex}`);
    if (!hideCaseNumber) {
      const caseNumberString = caseNumber ? `案號：${caseNumber}` : '';
      worksheet.getCell(`N${rowIndex}`).value = caseNumberString;
      worksheet.getCell(`N${rowIndex}`).font = { size: 9, name: fontName };
      worksheet.getCell(`N${rowIndex}`).alignment = { horizontal: 'left', vertical: 'middle' };
    }
    worksheet.getRow(rowIndex++).height = 18;

    worksheet.mergeCells(`A${rowIndex}:E${rowIndex}`);
    worksheet.getCell(`A${rowIndex}`).value = `此致 ${getSanitizedElderName(elderName)} 台照`;
    worksheet.getCell(`A${rowIndex}`).font = { bold: true, size: 10, name: fontName };
    worksheet.getCell(`A${rowIndex}`).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(`N${rowIndex}:R${rowIndex}`);
    if (!hideCmsLevel) {
      const cmsLevelString = cmsLevel ? `失能等級：${cmsLevel}` : '';
      worksheet.getCell(`N${rowIndex}`).value = cmsLevelString;
      worksheet.getCell(`N${rowIndex}`).font = { size: 9, name: fontName };
      worksheet.getCell(`N${rowIndex}`).alignment = { horizontal: 'left', vertical: 'middle' };
    }
    worksheet.getRow(rowIndex++).height = 18;

    worksheet.mergeCells(`A${rowIndex}:C${rowIndex}`);
    worksheet.getRow(rowIndex).font = { size: 9, name: fontName };
    worksheet.getRow(rowIndex).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.getCell(`A${rowIndex}`).value = '服務編號';
    worksheet.mergeCells(`D${rowIndex}:F${rowIndex}`);
    worksheet.getCell(`D${rowIndex}`).value = '福利身份別';
    worksheet.mergeCells(`G${rowIndex}:H${rowIndex}`);
    worksheet.getCell(`G${rowIndex}`).value = '原價';
    worksheet.mergeCells(`I${rowIndex}:J${rowIndex}`);
    worksheet.getCell(`I${rowIndex}`).value = '部分負擔';
    worksheet.mergeCells(`K${rowIndex}:L${rowIndex}`);
    worksheet.getCell(`K${rowIndex}`).value = '使用次數';
    worksheet.mergeCells(`M${rowIndex}:N${rowIndex}`);
    worksheet.getCell(`M${rowIndex}`).value = '自費單價';
    worksheet.mergeCells(`O${rowIndex}:P${rowIndex}`);
    worksheet.getCell(`O${rowIndex}`).value = '自費次數';
    worksheet.mergeCells(`Q${rowIndex}:R${rowIndex}`);
    worksheet.getCell(`Q${rowIndex}`).value = '小計';
    worksheet.getCell(`A${rowIndex}`).border = { top: { style: 'thick' }, right: { style: 'thin' }, left: { style: 'thick' } };
    ['D', 'G', 'I', 'K', 'M', 'O'].forEach((col) => {
      worksheet.getCell(`${col}${rowIndex}`).border = { top: { style: 'thick' }, right: { style: 'thin' } };
    });
    worksheet.getCell(`Q${rowIndex}`).border = { top: { style: 'thick' }, right: { style: 'thick' } };
    worksheet.getRow(rowIndex++).height = defaultRowHeight;

    elderRows.forEach((elderRow) => {
      worksheet.mergeCells(`A${rowIndex}:C${rowIndex}`);
      worksheet.getRow(rowIndex).font = { size: 9, name: fontName };
      worksheet.getRow(rowIndex).alignment = { horizontal: 'left', vertical: 'middle' };
      worksheet.getCell(`A${rowIndex}`).value = elderRow['服務編號'];
      worksheet.mergeCells(`D${rowIndex}:F${rowIndex}`);
      worksheet.getCell(`D${rowIndex}`).value = elderRow['福利身份別'];
      worksheet.mergeCells(`G${rowIndex}:H${rowIndex}`);
      worksheet.mergeCells(`I${rowIndex}:J${rowIndex}`);
      worksheet.mergeCells(`K${rowIndex}:L${rowIndex}`);
      worksheet.mergeCells(`M${rowIndex}:N${rowIndex}`);
      worksheet.getCell(`M${rowIndex}`).value = elderRow['自費單價'];
      worksheet.mergeCells(`O${rowIndex}:P${rowIndex}`);
      worksheet.getCell(`O${rowIndex}`).value = elderRow['自費次數'];
      worksheet.mergeCells(`Q${rowIndex}:R${rowIndex}`);
      worksheet.getCell(`Q${rowIndex}`).value = elderRow['小計'];

      worksheet.getCell(`A${rowIndex}`).border = { top: { style: 'thin' }, right: { style: 'thin' }, left: { style: 'thick' } };
      ['D', 'G', 'I', 'K', 'M', 'O'].forEach((col) => {
        worksheet.getCell(`${col}${rowIndex}`).border = { top: { style: 'thin' }, right: { style: 'thin' } };
      });
      worksheet.getCell(`Q${rowIndex}`).border = { top: { style: 'thin' }, right: { style: 'thick' } };
      worksheet.getRow(rowIndex++).height = defaultRowHeight;
    });

    worksheet.mergeCells(`A${rowIndex}:C${rowIndex}`);
    worksheet.getCell(`A${rowIndex}`).value = `服務天數：${serviceDays}`;
    worksheet.getCell(`A${rowIndex}`).font = { size: 9, name: fontName };
    worksheet.getCell(`A${rowIndex}`).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.getCell(`A${rowIndex}`).border = { top: { style: 'thin' }, right: { style: 'thin' }, left: { style: 'thick' } };
    worksheet.mergeCells(`D${rowIndex}:R${rowIndex}`);
    worksheet.getCell(`D${rowIndex}`).value = serviceDatesString;
    worksheet.getCell(`D${rowIndex}`).font = { size: 9, name: 'Calibri' };
    worksheet.getCell(`D${rowIndex}`).alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
    worksheet.getCell(`D${rowIndex}`).border = {
      top: { style: 'thin' }, right: { style: 'thick' }, left: { style: 'thin' }, bottom: { style: 'thin' },
    };
    worksheet.getRow(rowIndex++).height = defaultRowHeight * 5;

    const clientInfo = [`地址：${formatAddress(clientAddress, { includeZipCode: false })}`];
    if (taxIdNumber) {
      clientInfo.push(`統一編號： ${taxIdNumber}`);
    }
    if (phoneNumber) {
      clientInfo.push(`電話：${phoneNumber}`);
    }
    if (faxNumber) {
      clientInfo.push(`傳真：${faxNumber}`);
    }
    if (displayedBankName) {
      clientInfo.push(`銀行名稱：${displayedBankName}`);
    }
    if (displayedAccountNumber) {
      clientInfo.push(`匯款帳號：${displayedAccountNumber}`);
    }
    if (displayedAccountName) {
      clientInfo.push(`帳戶名：${displayedAccountName}`);
    }
    if (note) {
      clientInfo.push(`備註：${note}`);
    }
    const rowHeights = [];
    worksheet.mergeCells(`A${rowIndex}:F${rowIndex + 7}`);
    const clientInfoString = clientInfo.join('\n');
    worksheet.getCell(`A${rowIndex}`).value = clientInfoString;
    worksheet.getCell(`A${rowIndex}`).font = { size: 9, name: fontName };
    worksheet.getCell(`A${rowIndex}`).alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
    worksheet.getCell(`A${rowIndex}`).border = { top: { style: 'thin' }, right: { style: 'thin' }, left: { style: 'thick' } };
    worksheet.mergeCells(`G${rowIndex}:N${rowIndex + 3}`);
    worksheet.getCell(`G${rowIndex}`).value = `費用總計：$ ${total}`;
    worksheet.getCell(`G${rowIndex}`).font = { bold: true, size: 9.5, name: fontName };
    worksheet.getCell(`G${rowIndex}`).alignment = { vertical: 'middle' };
    worksheet.getCell(`G${rowIndex}`).border = { top: { style: 'thin' }, right: { style: 'thin' }, left: { style: 'thin' } };
    worksheet.mergeCells(`O${rowIndex}:R${rowIndex + 7}`);
    worksheet.getCell(`O${rowIndex}`).border = { top: { style: 'thin' }, right: { style: 'thick' }, left: { style: 'thin' } };
    for (let i = 0; i < 4; i++) {
      worksheet.getRow(rowIndex++).height = rowHeights[i] || defaultRowHeight; // NOSONAR
    }

    worksheet.mergeCells(`G${rowIndex}:N${rowIndex + 3}`);
    worksheet.getCell(`G${rowIndex}`).value = `茲收到新台幣 ${numberToChineseUpper(total)} 元整`;
    worksheet.getCell(`G${rowIndex}`).font = { size: 9, name: fontName };
    worksheet.getCell(`G${rowIndex}`).alignment = { vertical: 'middle' };
    worksheet.getCell(`G${rowIndex}`).border = { top: { style: 'thin' }, right: { style: 'thin' }, left: { style: 'thin' } };
    for (let i = 0; i < 4; i++) {
      worksheet.getRow(rowIndex++).height = rowHeights[i + 4] || defaultRowHeight; // NOSONAR
    }

    worksheet.mergeCells(`S${rowIndex - 10 - elderRowsCount}:S${rowIndex - 1}`);
    worksheet.getCell(`S${rowIndex - 10 - elderRowsCount}`).value = copyType[receiptType][copyCount];
    worksheet.getCell(`S${rowIndex - 10 - elderRowsCount}`).font = { size: 6, name: fontName };
    worksheet.getCell(`S${rowIndex - 10 - elderRowsCount}`).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

    worksheet.mergeCells(`A${rowIndex}:C${rowIndex}`);
    if (stampImages.chairman && stampImages.chairman.imageId !== undefined) {
      worksheet.getCell(`A${rowIndex}`).value = managerTitle ? `${managerTitle}：` : '理事長：';
    } else {
      worksheet.getCell(`A${rowIndex}`).value = managerTitle ? `${managerTitle}： ${chairmanName || ''}` : `理事長： ${chairmanName || ''}`;
    }
    worksheet.mergeCells(`G${rowIndex}:I${rowIndex}`);
    if (!hideAccountant) {
      if (stampImages.accountant && stampImages.accountant.imageId !== undefined) {
        worksheet.getCell(`G${rowIndex}`).value = '會計：';
      } else {
        worksheet.getCell(`G${rowIndex}`).value = `會計： ${accountantName || ''}`;
      }
    }
    worksheet.mergeCells(`M${rowIndex}:O${rowIndex}`);
    if (stampImages.handler && stampImages.handler.imageId !== undefined) {
      worksheet.getCell(`M${rowIndex}`).value = '經手人：';
    } else {
      worksheet.getCell(`M${rowIndex}`).value = `經手人： ${handlerName || ''}`;
    }
    worksheet.getRow(rowIndex).font = { size: 9, name: fontName };
    worksheet.getRow(rowIndex).alignment = { horizontal: 'left', vertical: 'middle' };
    ['A', 'C', 'D', 'E', 'F', 'G', 'I', 'J', 'K', 'L', 'M', 'O', 'P', 'Q', 'R'].forEach((col) => {
      worksheet.getCell(`${col}${rowIndex}`).border = { top: { style: 'thick' } };
    });
    if (stampImages.company && stampImages.company.imageId !== undefined) {
      worksheet.addImage(stampImages.company.imageId, {
        tl: { col: 14 + 0.3, row: rowIndex - 9 + 0.3 },
        ext: { width: stampImages.company.width, height: stampImages.company.height },
      });
    }
    if (stampImages.chairman && stampImages.chairman.imageId !== undefined) {
      worksheet.addImage(stampImages.chairman.imageId, {
        tl: { col: 1 + 0.3, row: rowIndex - 1 + 0.3 },
        ext: { width: stampImages.chairman.width, height: stampImages.chairman.height },
      });
    }
    if (!hideAccountant && stampImages.accountant && stampImages.accountant.imageId !== undefined) {
      worksheet.addImage(stampImages.accountant.imageId, {
        tl: { col: 7 + 0.3, row: rowIndex - 1 + 0.3 },
        ext: { width: stampImages.accountant.width, height: stampImages.accountant.height },
      });
    }
    if (stampImages.handler && stampImages.handler.imageId !== undefined) {
      worksheet.addImage(stampImages.handler.imageId, {
        tl: { col: 13 + 0.3, row: rowIndex - 1 + 0.3 },
        ext: { width: stampImages.handler.width, height: stampImages.handler.height },
      });
    }
    worksheet.getRow(rowIndex++).height = defaultRowHeight;

    worksheet.getRow(rowIndex++).height = defaultRowHeight;
    worksheet.getRow(rowIndex++).height = defaultRowHeight;

    copyCount++;

    if (copyCount < copyType[receiptType].length) {
      ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S'].forEach((col) => {
        worksheet.getCell(`${col}${rowIndex}`).border = { bottom: { style: 'dashDot' } };
      });
      worksheet.getRow(rowIndex++).height = defaultRowHeight;
      worksheet.getRow(rowIndex++).height = defaultRowHeight;
      worksheet.getRow(rowIndex++).height = defaultRowHeight;
    }
  }

  worksheet.pageSetup.printArea = `A1:S${rowIndex}`;

  return worksheet;
}
